import { useState, useEffect } from 'react';
import { X, Plus, Edit, Trash2, Shield } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useEmpresa } from '../contexts/EmpresaContext';
import api from '../api/config';
import './CatalogModal.css';

const CatalogModal = ({ isOpen, onClose, companyId }) => {
  const { isSuperAdmin } = useSelector(state => state.auth);
  const { empresaActiva } = useEmpresa();

  // Obtener el rol del usuario desde la empresa activa
  const userRole = empresaActiva?.rolUsuario || empresaActiva?.role;

  const [activeTab, setActiveTab] = useState('positions');
  const [themeColor, setThemeColor] = useState('#c4fffc');

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  // Verificar permisos de edición
  // - Cargos: admin, administrador y editor pueden editar
  // - Roles: solo super admin puede editar
  const canEditPositions = isSuperAdmin || userRole === 'admin' || userRole === 'administrador' || userRole === 'editor';
  const canEditRoles = isSuperAdmin; // Solo super admin puede editar roles
  const [positions, setPositions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Traducciones de roles del sistema
  const roleTranslations = {
    'admin': 'Administrador',
    'editor': 'Editor',
    'member': 'Miembro'
  };

  useEffect(() => {
    if (isOpen) {
      loadCatalogs();
    }
  }, [isOpen]);

  const loadCatalogs = async () => {
    setLoading(true);
    try {
      const [posRes, roleRes] = await Promise.all([
        api.get('/catalog/positions', { params: { companyId } }),
        api.get('/catalog/roles')
      ]);

      setPositions(posRes.data.data || []);
      // Filtrar superadmin - es interno del sistema
      const filteredRoles = (roleRes.data.data || []).filter(role => role.name !== 'superadmin');
      setRoles(filteredRoles);
    } catch (error) {
      console.error('Error loading catalogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const endpoint = activeTab === 'positions' ? '/catalog/positions' : '/catalog/roles';

    try {
      const dataToSend = { ...formData };
      // Solo agregar companyId si estamos trabajando con posiciones
      if (activeTab === 'positions' && companyId) {
        dataToSend.companyId = companyId;
      }

      if (editingItem) {
        await api.put(`${endpoint}/${editingItem.id}`, dataToSend);
      } else {
        await api.post(endpoint, dataToSend);
      }

      loadCatalogs();
      setFormData({ name: '', description: '' });
      setEditingItem(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error al guardar');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, description: item.description || '' });
    setShowForm(true);
  };

  const handleDelete = (item) => {
    setDeleteConfirm(item);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    const endpoint = activeTab === 'positions' ? '/catalog/positions' : '/catalog/roles';

    try {
      await api.delete(`${endpoint}/${deleteConfirm.id}`);
      loadCatalogs();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error al eliminar');
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleCancel = () => {
    setEditingItem(null);
    setFormData({ name: '', description: '' });
    setShowForm(false);
  };

  if (!isOpen) return null;

  const currentItems = activeTab === 'positions' ? positions : roles;

  return (
    <div className="catalog-modal-overlay" onClick={onClose}>
      <div className="catalog-modal" onClick={(e) => e.stopPropagation()}>
        <div className="catalog-modal-header">
          <h2>Gestionar Cargos y Permisos</h2>
          <button className="catalog-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="catalog-tabs">
          <button
            className={`catalog-tab ${activeTab === 'positions' ? 'active' : ''}`}
            onClick={() => setActiveTab('positions')}
            style={activeTab === 'positions' ? {
              backgroundColor: themeColor,
              color: '#000',
              border: '2px solid #000'
            } : {}}
          >
            Cargos
          </button>
          {isSuperAdmin && (
            <button
              className={`catalog-tab ${activeTab === 'roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('roles')}
              style={activeTab === 'roles' ? {
                backgroundColor: themeColor,
                color: '#000',
                border: '2px solid #000'
              } : {}}
            >
              Permisos de Usuario
            </button>
          )}
        </div>

        <div className="catalog-content">
          {/* Confirmación de eliminación - Se muestra en lugar del contenido normal */}
          {deleteConfirm ? (
            <div className="delete-confirm-section">
              <div className="delete-confirm-icon" style={{
                width: '80px',
                height: '80px',
                backgroundColor: themeColor,
                border: '3px solid #000',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Trash2 size={48} style={{ color: '#000' }} />
              </div>
              <h3>¿Eliminar {activeTab === 'positions' ? 'Cargo' : 'Rol'}?</h3>
              <p>¿Estás seguro de que deseas eliminar <strong>"{deleteConfirm.name}"</strong>?</p>
              {deleteConfirm.description && (
                <p className="delete-item-description" style={{
                  color: '#000',
                  fontSize: '14px',
                  marginTop: '8px',
                  backgroundColor: themeColor,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '2px solid #000',
                  fontWeight: '600'
                }}>
                  {deleteConfirm.description}
                </p>
              )}
              <p className="delete-warning" style={{
                color: '#000',
                fontWeight: '600',
                marginTop: '16px',
                fontSize: '14px'
              }}>
                ⚠️ Esta acción no se puede deshacer.
              </p>
              <div className="delete-confirm-actions" style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                marginTop: '24px'
              }}>
                <button
                  className="btn-cancel-delete"
                  onClick={cancelDelete}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#fff';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                  style={{
                    backgroundColor: '#fff',
                    color: '#000',
                    border: '2px solid #000',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="btn-confirm-delete"
                  onClick={confirmDelete}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#000';
                    e.target.style.color = themeColor;
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = themeColor;
                    e.target.style.color = '#000';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                  style={{
                    backgroundColor: themeColor,
                    color: '#000',
                    border: '2px solid #000',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Trash2 size={16} />
                  Sí, eliminar
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Botón para agregar nuevo item - solo si tiene permisos */}
              {!showForm && !editingItem && (
                (activeTab === 'positions' && canEditPositions) ||
                (activeTab === 'roles' && canEditRoles)
              ) && (
                <button
                  className="btn-show-form"
                  onClick={() => setShowForm(true)}
                  style={{
                    backgroundColor: themeColor,
                    color: '#000',
                    border: '2px solid #000'
                  }}
                >
                  <Plus size={18} />
                  Agregar {activeTab === 'positions' ? 'Cargo' : 'Rol'}
                </button>
              )}

              {/* Botón para cerrar formulario cuando está abierto */}
              {showForm && !editingItem && (
                <button
                  className="btn-close-form"
                  onClick={() => setShowForm(false)}
                >
                  <X size={18} />
                  Cerrar formulario
                </button>
              )}

              {/* Mostrar formulario para cargos (admin/editor) o roles (solo superadmin) */}
              {showForm && (
                (activeTab === 'positions' && canEditPositions) ||
                (activeTab === 'roles' && canEditRoles)
              ) && (
                <form onSubmit={handleSubmit} className="catalog-form">
                  <div className="catalog-form-row">
                    <div className="catalog-form-inputs">
                      <input
                        type="text"
                        placeholder={activeTab === 'positions' ? 'Nombre del cargo' : 'Nombre del rol'}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                      <textarea
                        placeholder="Descripción (opcional)"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows="2"
                        className="description-textarea"
                      />
                    </div>
                    <div className="catalog-form-buttons">
                      {editingItem ? (
                        <>
                          <button type="submit" className="btn-add-item">
                            <Edit size={18} />
                            Actualizar
                          </button>
                          <button type="button" className="btn-cancel" onClick={handleCancel}>
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <button type="submit" className="btn-add-item">
                          <Plus size={18} />
                          Agregar
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              )}

              {/* Mensaje informativo para roles cuando no es super admin */}
              {activeTab === 'roles' && !canEditRoles && (
                <div style={{
                  backgroundColor: '#f0f8ff',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  borderLeft: `4px solid ${themeColor}`,
                  color: '#333'
                }}>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    <strong>Información:</strong> Los roles del sistema definen los permisos de acceso.
                    Solo el Super Administrador puede modificarlos.
                  </p>
                </div>
              )}

              <div className="catalog-list">
                {loading ? (
                  <p>Cargando...</p>
                ) : (
                  currentItems.map(item => (
                    <div key={item.id} className="catalog-item">
                      <div className="catalog-item-info">
                        {activeTab === 'roles' && item.level >= 70 && (
                          <Shield size={16} className="role-icon" />
                        )}
                        <div>
                          <h4>{activeTab === 'roles' ? (roleTranslations[item.name] || item.name) : item.name}</h4>
                          {item.description && <p>{item.description}</p>}
                        </div>
                      </div>
                      <div className="catalog-item-actions">
                        {/* Cargos: admin/editor pueden editar y eliminar. Roles: solo mostrar como solo lectura */}
                        {activeTab === 'positions' && canEditPositions ? (
                          <>
                            <button
                              className="btn-edit-item"
                              onClick={() => handleEdit(item)}
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn-delete-item"
                              onClick={() => handleDelete(item)}
                              title="Eliminar"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        ) : (
                          <span className="read-only-label">Solo lectura</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CatalogModal;